/*******************************************************************************
 * Copyright (c) 2024-2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {DestroyRef, Injectable, OnDestroy, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LocalStorageService} from '@common-services/local-storage/local-storage.service';
import {
  BehaviorSubject,
  filter,
  from,
  fromEvent,
  map,
  mergeMap,
  Observable,
  repeat,
  Subject,
  takeUntil,
  takeWhile,
  throttleTime,
  timer,
} from 'rxjs';

type UserActivity = {
  lastUserActive: Date;
  active: boolean;
};

@Injectable()
export class UserActivityService implements OnDestroy {
  private localStorageService = inject(LocalStorageService);

  private localStorageKey: string = 'userActivity';
  private trackedEvents: string[] = ['mousemove', 'mousewheel', 'click', 'keydown'];
  private stop = new Subject<void>(); // Observable to stop the timer
  private start = new Subject<void>(); // Observable to start the timer
  private userActive: BehaviorSubject<boolean>; // Observable to control if the user is active
  public userActive$: Observable<boolean>; // Observable to share the user activity status

  private checkUserActivityInterval: number = 1000 * 5; // 5 seconds in milliseconds
  private maximumInactiveTime: number = 20 * 60; // 20 minutes in seconds by default

  private readonly destroyRef = inject(DestroyRef);

  private userActivity: UserActivity = {
    lastUserActive: new Date(),
    active: true,
  };

  constructor() {
    this.userActive = new BehaviorSubject<boolean>(this.userActivity.active);
    this.userActive$ = this.userActive.asObservable();

    // read local storage and update user activity
    const userActivity = this.localStorageService.getItem(this.localStorageKey);
    if (userActivity) {
      this.userActivity = JSON.parse(userActivity);
      this.userActive.next(this.userActivity.active);
    } else {
      // set an expired user activity if not found in local storage because it means that the user deleted the local intentionally
      // on the login, Auth Effects stores the user activity in the local storage after MFA verification
      this.userActivity.active = false;
      this.userActivity.lastUserActive = new Date(
        new Date().getTime() - 2 * this.maximumInactiveTime * 1000 // two times the maximum inactive time
      );
      this.storeUserActivity();
    }

    this.updateLastUserActivity();
    this.createUserActivityTimer();
  }

  ngOnDestroy(): void {
    this.stopUserActivityTimer();
  }

  /**
   * Store the user login activity in the local storage
   */
  storeUserLoginActivity(): void {
    const userActivity = {
      lastUserActive: new Date(),
      active: true,
    };
    this.localStorageService.setItem(this.localStorageKey, JSON.stringify(userActivity));
  }

  /**
   * Start the service
   */
  setUp(): void {
    // listen to the tracked events to update the last user activity
    from(this.trackedEvents)
      .pipe(
        mergeMap(event => fromEvent(document, event)),
        takeUntilDestroyed(this.destroyRef),
        throttleTime(this.checkUserActivityInterval)
      )
      .subscribe(() => {
        this.updateLastUserActivity();
      });

    // use storage event to update last user activity from other tabs
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event.key === this.localStorageKey && !!event.newValue),
        map(event => JSON.parse(event.newValue!))
      )
      .subscribe(data => {
        this.userActivity = data;
        this.userActive.next(this.userActivity.active);
        if (this.userActivity.active) {
          this.restartUserActivityTimer();
        }
      });
  }

  /**
   * Restart the timer
   */
  private restartUserActivityTimer(): void {
    this.stopUserActivityTimer();
    this.start.next();
  }

  /**
   * Stop the timer
   */
  private stopUserActivityTimer(): void {
    this.stop.next();
  }

  /**
   * Update the last user activity
   */
  private updateLastUserActivity(): void {
    if (
      this.userActivity.active &&
      new Date().getTime() < this.getExpirationInMilliseconds()
    ) {
      this.userActivity.lastUserActive = new Date();
      this.storeUserActivity();
      this.restartUserActivityTimer();
    } else {
      this.setUserAsInactive();
    }
  }

  /**
   * Store the user activity in the local storage
   */
  private storeUserActivity(): void {
    this.localStorageService.setItem(this.localStorageKey, JSON.stringify(this.userActivity));
  }

  /**
   * Create the user activity timer
   */
  private createUserActivityTimer(): void {
    timer(0, 1000)
      .pipe(
        map(index => this.maximumInactiveTime - index),
        takeWhile(remainSeconds => remainSeconds >= 0),
        takeUntil(this.stop),
        repeat({delay: () => this.start})
      )
      .subscribe(remainSeconds => {
        if (remainSeconds === 0) {
          this.setUserAsInactive();
        }
      });
  }

  /**
   * Set the user as inactive
   */
  setUserAsInactive(): void {
    this.userActivity.active = false;
    this.userActive.next(this.userActivity.active);
    this.storeUserActivity();
    this.stopUserActivityTimer();
  }

  /**
   * Reset the user activity
   */
  resetUserActivity(): void {
    this.userActivity.active = true;
    this.userActivity.lastUserActive = new Date();
    this.userActive.next(this.userActivity.active);
    this.storeUserActivity();
    this.restartUserActivityTimer();
  }

  /**
   * Get the user expiration in milliseconds
   * @returns number
   */
  getExpirationInMilliseconds(): number {
    return (
      new Date(this.userActivity.lastUserActive).getTime() + this.maximumInactiveTime * 1000
    );
  }
}
